import React from 'react';
import { Loader2 } from 'lucide-react';

export function Progress({ value = 0, className = "", ...props }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className={`progress ${className}`} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} {...props}>
      <div style={{ width: `${pct}%` }} />
    </div>
  );
}

export function ProgressIndicator({
  message = "Processing...",
  progress = null,
  showPercentage = false,
  size = "default"
}) {
  const sizes = {
    small: { spinner: 14, text: 12, padding: 8 },
    default: { spinner: 16, text: 13, padding: 10 },
    large: { spinner: 18, text: 14, padding: 12 }
  };
  const s = sizes[size] || sizes.default;

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: s.padding,
        padding: s.padding,
        background: 'var(--bg-inset)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        fontSize: s.text,
        color: 'var(--fg-secondary)'
      }}
    >
      <Loader2 size={s.spinner} style={{ animation: 'spin 0.8s linear infinite', color: 'var(--accent)' }} />
      <div style={{ flex: 1 }}>
        {message}
        {progress !== null && showPercentage && (
          <span className="tabular" style={{ marginLeft: 8, fontWeight: 600, color: 'var(--fg)' }}>
            {Math.round(progress)}%
          </span>
        )}
      </div>
      {progress !== null && <Progress value={progress} style={{ width: 60, height: 4 }} />}
    </div>
  );
}

export function ImportProgress({
  isImporting,
  currentCount = 0,
  totalExpected = null,
  operation = "Importing"
}) {
  if (!isImporting) return null;

  const progress = totalExpected ? (currentCount / totalExpected) * 100 : null;

  const getMessage = () => {
    if (totalExpected) {
      return `${operation} issues... (${currentCount}/${totalExpected})`;
    }
    return currentCount > 0
      ? `${operation} issues... (${currentCount} imported)`
      : `${operation} issues...`;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
        minWidth: '320px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: 'var(--shadow-lg)'
      }}
    >
      <ProgressIndicator
        message={getMessage()}
        progress={progress}
        showPercentage={totalExpected !== null}
        size="large"
      />

      <div style={{
        marginTop: '12px',
        fontSize: '12px',
        color: 'var(--fg-muted)',
        textAlign: 'center'
      }}>
        Please wait while we process your import...
      </div>
    </div>
  );
}

export function InlineProgress({
  isActive,
  message = "Loading...",
  progress = null
}) {
  if (!isActive) return null;

  return (
    <ProgressIndicator
      message={message}
      progress={progress}
      showPercentage={false}
      size="small"
    />
  );
}
